/**
 * /app/settings/printers — Thermal Printing Setup Center
 *
 * Allows the tenant to:
 * 1. Check QZ Tray status
 * 2. Detect local printers
 * 3. Assign printers to logical roles
 * 4. Run a test print
 *
 * IMPORTANT: All printing is client-side only.
 * No ESC/POS data goes through the backend.
 * Flow: Browser → QZ Tray → ESC/POS → Thermal printer
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  Printer,
  RefreshCw,
  Wifi,
  WifiOff,
  Download,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  RotateCcw,
  Trash2,
  PlayCircle,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useQz, qzStatusLabel, qzStatusColor } from "@/lib/printing/use-qz";
import { QZ_PUBLIC_CERT } from "@/lib/printing/qz-cert";
import {
  loadPrinterConfig,
  savePrinterConfig,
  resetPrinterConfig,
  paperWidthChars,
} from "@/lib/printing/printer-service";
import { buildTestPrintEscPos } from "@/lib/printing/escpos-builder";
import { useTenantContextSafe } from "@/lib/tenant-context";
import {
  PRINTER_ROLE_LABELS,
  PRINTER_ROLE_DESCRIPTIONS,
  type PrinterRole,
  type QzPrinterConfig,
} from "@/lib/printing/qz-types";
import { useTenantContext } from "@/lib/tenant-context";
import { ClinicalPageShell } from "@/components/clinical";
import { SettingsSectionBack } from "@/components/settings/SettingsSectionHub";
import { PrinterSetupTour } from "@/components/ProductTour";
import { DataTable, CrudShellHeader, Panel, QuickActions } from "@/components/ds";
import type { DataTableColumn, QuickAction } from "@/components/ds";

export const Route = createFileRoute("/app/settings/printers")({
  component: PrinterSettingsPage,
  head: () => ({ meta: [{ title: "Impresoras — Structura Market" }] }),
});

// ── Constants ─────────────────────────────────────────────────────────────────

const PRINTER_ROLES: PrinterRole[] = [
  "cashier_printer",
  "kitchen_printer",
  "bar_printer",
  "label_printer",
  "backup_printer",
];

const DRIVER_CARDS = [
  {
    name: "Epson TM Series",
    logo: "🖨️",
    description: "TM-T20, TM-T88, TM-T82 — las más usadas en gastronomía",
    url: "https://download.epson-biz.com/modules/pos/",
    compat: ["Windows", "macOS"],
    popular: true,
  },
  {
    name: "XPrinter / XP-58 / XP-80",
    logo: "🖨️",
    description: "Impresoras económicas chinas — driver Universal ESC/POS",
    url: "https://www.xprintertech.com/driver",
    compat: ["Windows"],
    popular: false,
  },
  {
    name: "Genérico ESC/POS",
    logo: "⚙️",
    description: "Cualquier impresora compatible con ESC/POS (Bixolon, Star, etc.)",
    url: "https://github.com/qzind/tray/wiki/printer-setup",
    compat: ["Windows", "macOS", "Linux"],
    popular: false,
  },
];

const QZ_DOWNLOAD_URL = "https://qz.io/download/";

/**
 * Descarga el certificado público de Structura como override.crt.
 * QZ Tray SIEMPRE muestra el popup "Untrusted website" en la primera conexión
 * de una PC nueva con un certificado autofirmado — firmar cada request (ya
 * implementado en use-qz.ts) evita que vuelva a pedirlo una vez aceptado, pero
 * no elimina ese primer aviso. La única forma de que NUNCA aparezca (ni la
 * primera vez) es que QZ Tray confíe en el certificado desde antes de conectar,
 * vía este archivo — mecanismo de "trusted root cert" documentado oficialmente
 * por QZ Tray (github.com/qzind/tray/wiki/Deployment).
 */
function downloadQzOverrideCert() {
  const blob = new Blob([QZ_PUBLIC_CERT], { type: "application/x-x509-ca-cert" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "override.crt";
  a.click();
  URL.revokeObjectURL(url);
}

// ── Main Page ─────────────────────────────────────────────────────────────────

function PrinterSettingsPage() {
  const printCtx = useTenantContextSafe();
  const thermalEntitled = printCtx?.productFlags?.thermal_printing === true;
  const navigate = useNavigate();
  const { tenantName } = useTenantContext();
  const { status, connect, disconnect, findPrinters, print, isReady, error } = useQz();

  const [foundPrinters, setFoundPrinters] = useState<string[]>([]);
  const [scanning, setScanning] = useState(false);
  const [testPrinting, setTestPrinting] = useState<PrinterRole | null>(null);
  const [config, setConfig] = useState(loadPrinterConfig);
  const [dirty, setDirty] = useState(false);

  // Reload config from localStorage on mount (SSR-safe)
  useEffect(() => {
    setConfig(loadPrinterConfig());
  }, []);

  // ── Actions ─────────────────────────────────────────────────────────────────

  const handleScan = useCallback(async () => {
    if (!isReady) {
      toast.error("Conectá QZ Tray primero");
      return;
    }
    setScanning(true);
    try {
      const printers = await findPrinters();
      setFoundPrinters(printers);
      if (printers.length === 0) {
        toast.warning("No se detectaron impresoras. Verificá que estén encendidas.");
      } else {
        toast.success(`${printers.length} impresora${printers.length > 1 ? "s" : ""} detectada${printers.length > 1 ? "s" : ""}`);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al escanear");
    } finally {
      setScanning(false);
    }
  }, [isReady, findPrinters]);

  const handleSave = useCallback(() => {
    savePrinterConfig(config);
    setDirty(false);
    toast.success("Configuración de impresoras guardada");
  }, [config]);

  const handleReset = useCallback(() => {
    resetPrinterConfig();
    setConfig(loadPrinterConfig());
    setDirty(false);
    toast.info("Configuración restablecida");
  }, []);

  const updateRolePrinter = useCallback(
    (role: PrinterRole, field: keyof QzPrinterConfig, value: unknown) => {
      setConfig((prev) => {
        const existing = prev.printers[role] ?? {
          role,
          printerName: "",
          paperWidth: "80mm" as const,
          autoCut: true,
          copies: 1,
          enabled: true,
        };
        return {
          ...prev,
          printers: {
            ...prev.printers,
            [role]: { ...existing, [field]: value },
          },
        };
      });
      setDirty(true);
    },
    [],
  );

  const removeRolePrinter = useCallback((role: PrinterRole) => {
    setConfig((prev) => {
      const { [role]: _, ...rest } = prev.printers;
      return { ...prev, printers: rest };
    });
    setDirty(true);
  }, []);

  const handleTestPrint = useCallback(
    async (role: PrinterRole) => {
      const cfg = config.printers[role];
      if (!cfg?.printerName) {
        toast.error("Asigná una impresora a este rol primero");
        return;
      }
      if (!isReady) {
        toast.error("QZ Tray no está conectado");
        return;
      }
      setTestPrinting(role);
      try {
        const width = paperWidthChars(cfg.paperWidth);
        const escpos = buildTestPrintEscPos(
          {
            tenantName: tenantName ?? "Mi Negocio",
            printerRole: PRINTER_ROLE_LABELS[role],
          },
          width,
        );
        await print(cfg.printerName, [{ type: "raw", format: "plain", data: escpos }], cfg.copies);
        toast.success(`Test enviado a ${cfg.printerName}`);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Error al imprimir");
      } finally {
        setTestPrinting(null);
      }
    },
    [config.printers, isReady, print, tenantName],
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  const scanAction: QuickAction[] = [
    {
      key: "scan",
      label: scanning ? "Buscando…" : "Buscar impresoras",
      icon: scanning ? Loader2 : RefreshCw,
      spinning: scanning,
      variant: "outline",
      size: "sm",
      disabled: scanning || !isReady,
      onClick: handleScan,
    },
  ];

  const saveAction: QuickAction[] = [
    { key: "save", label: "Guardar cambios", icon: CheckCircle2, size: "sm", onClick: handleSave },
  ];

  const foundPrintersColumns: DataTableColumn<string>[] = [
    {
      key: "nombre",
      header: "Impresora",
      render: (p) => (
        <div className="flex items-center gap-3">
          <Printer className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="font-mono">{p}</span>
          <Badge variant="outline" className="ml-auto text-[10px]">
            Detectada
          </Badge>
        </div>
      ),
    },
  ];

  if (!thermalEntitled) {
    return (
      <div className="p-6 text-sm text-muted-foreground">Requiere la feature thermal_printing en tu plan.</div>
    );
  }

  return (
    <div>
      <CrudShellHeader title="Impresoras" />
      <ClinicalPageShell offset="none">
        <div className="mx-auto w-full max-w-3xl space-y-4 p-4">
        <SettingsSectionBack
          title="Impresoras"
          description="Configurá impresoras térmicas, comandas y tickets del workspace"
          onBack={() => void navigate({ to: "/app/settings", search: {} as never })}
        />
        <PrinterSetupTour />
        <div className="space-y-8 pb-12">

          {/* ── PARTE 1: Estado de QZ Tray ──────────────────────────────── */}
          <div data-tour="qz-status">
            <Panel
              title="QZ Tray"
              description="Puente entre el navegador y la impresora local"
              actions={<QzStatusBadge status={status} />}
            >
              <QzStatusPanel
                status={status}
                error={error}
                onConnect={connect}
                onDisconnect={disconnect}
              />
            </Panel>
          </div>

          {/* ── PARTE 2: Drivers ────────────────────────────────────────── */}
          <div data-tour="printer-drivers">
            <Panel
              title="Drivers recomendados"
              description="Structura no instala drivers. Descargalos desde el sitio oficial del fabricante."
            >
              <div className="grid gap-3 sm:grid-cols-3">
                {DRIVER_CARDS.map((d) => (
                  <DriverCard key={d.name} {...d} />
                ))}
              </div>
            </Panel>
          </div>

          {/* ── PARTE 3: Detección ──────────────────────────────────────── */}
          <div data-tour="printer-scan">
            <Panel
              title="Detectar impresoras"
              description="Impresoras disponibles en este dispositivo"
              actions={<QuickActions actions={scanAction} />}
            >
              <div className="space-y-4">
                {!isReady && (
                  <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-400">
                    Conectá QZ Tray primero para detectar impresoras
                  </p>
                )}

                {foundPrinters.length > 0 && (
                  <DataTable columns={foundPrintersColumns} rows={foundPrinters} getRowKey={(p) => p} />
                )}
              </div>
            </Panel>
          </div>

          {/* ── PARTE 4: Configuración por rol ──────────────────────────── */}
          <div data-tour="printer-roles">
            <Panel
              title="Asignación de impresoras"
              description="Asigná una impresora a cada rol operacional"
              actions={dirty ? <QuickActions actions={saveAction} /> : undefined}
            >
              <div className="space-y-3">
                {PRINTER_ROLES.map((role) => (
                  <PrinterRoleCard
                    key={role}
                    role={role}
                    cfg={config.printers[role]}
                    foundPrinters={foundPrinters}
                    testPrinting={testPrinting === role}
                    isReady={isReady}
                    onUpdate={(field, value) => updateRolePrinter(role, field, value)}
                    onRemove={() => removeRolePrinter(role)}
                    onTest={() => handleTestPrint(role)}
                  />
                ))}
              </div>
            </Panel>
          </div>

          {/* ── Reset ───────────────────────────────────────────────────── */}
          <div className="flex justify-end pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-2 text-muted-foreground hover:text-destructive"
              onClick={handleReset}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Restablecer configuración de impresoras
            </Button>
          </div>
        </div>
      </div>
    </ClinicalPageShell>
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function QzStatusBadge({ status }: { status: ReturnType<typeof useQz>["status"] }) {
  const color = qzStatusColor(status);
  const label = qzStatusLabel(status);
  return (
    <span className={cn("flex items-center gap-1.5 text-xs font-medium", color)}>
      <span
        className={cn(
          "inline-block h-2 w-2 rounded-full",
          status === "connected" ? "bg-emerald-400" :
          status === "connecting" || status === "loading" ? "bg-amber-400 animate-pulse" :
          "bg-muted-foreground/50",
        )}
      />
      {label}
    </span>
  );
}

function QzStatusPanel({
  status,
  error,
  onConnect,
  onDisconnect,
}: {
  status: ReturnType<typeof useQz>["status"];
  error: string | null;
  onConnect: () => Promise<void>;
  onDisconnect: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);

  const handleConnect = async () => {
    setBusy(true);
    await onConnect();
    setBusy(false);
  };

  const handleDisconnect = async () => {
    setBusy(true);
    await onDisconnect();
    setBusy(false);
  };

  if (status === "connected") {
    return (
      <div className="flex items-center gap-3 rounded-lg bg-emerald-500/10 px-4 py-3">
        <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-emerald-300">QZ Tray conectado</p>
          <p className="text-xs text-emerald-400/70">
            Listo para imprimir sin ventanas del navegador
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="text-muted-foreground hover:text-foreground shrink-0"
          onClick={handleDisconnect}
          disabled={busy}
        >
          Desconectar
        </Button>
      </div>
    );
  }

  if (status === "connecting" || status === "loading") {
    return (
      <div className="flex items-center gap-3 rounded-lg bg-amber-500/10 px-4 py-3">
        <Loader2 className="h-4 w-4 text-amber-400 animate-spin shrink-0" />
        <p className="text-sm text-amber-300">
          {status === "loading" ? "Cargando QZ Tray…" : "Conectando…"}
        </p>
      </div>
    );
  }

  // Disconnected / error / not_loaded
  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-red-500/10 px-4 py-3">
          <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
          <p className="text-xs text-red-300">{error}</p>
        </div>
      )}

      <div className="rounded-lg border border-border/40 bg-background/40 p-5 space-y-4">
        <div className="flex items-start gap-3">
          <WifiOff className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground">
              Para imprimir sin ventanas del navegador,
              instalá QZ Tray <span className="text-primary font-semibold">una única vez</span>.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              QZ Tray corre en segundo plano y envía comandos ESC/POS directamente a tu impresora.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <a
            href={QZ_DOWNLOAD_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            <Download className="h-4 w-4" />
            Descargar QZ Tray
          </a>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleConnect}
            disabled={busy}
            className="gap-2"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wifi className="h-3.5 w-3.5" />}
            {busy ? "Conectando…" : "Ya lo tengo instalado"}
          </Button>
        </div>

        <div className="flex gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> macOS
          </span>
          <span className="flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> Windows
          </span>
          <span className="flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> Linux
          </span>
        </div>
      </div>

      <div className="rounded-lg border border-border/40 bg-background/40 p-5 space-y-3">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground">
              ¿Te aparece un aviso de "Untrusted website" al conectar QZ Tray?
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Es normal la primera vez en una PC nueva — QZ Tray no confía en ningún
              certificado hasta que se lo indicás una vez. Descargá el certificado de
              Structura y copialo en la carpeta de instalación de QZ Tray para que no
              vuelva a preguntar, ni siquiera la primera vez.
            </p>
          </div>
        </div>

        <Button type="button" variant="outline" size="sm" onClick={downloadQzOverrideCert} className="gap-2">
          <Download className="h-3.5 w-3.5" />
          Descargar certificado (override.crt)
        </Button>

        <div className="text-xs text-muted-foreground space-y-1 pl-1">
          <p>1. Descargá el archivo <code className="text-foreground">override.crt</code> con el botón de arriba.</p>
          <p>
            2. Copialo dentro de la carpeta donde instalaste QZ Tray, ej. en Windows:{" "}
            <code className="text-foreground">C:\Program Files\QZ Tray\override.crt</code>
          </p>
          <p>3. Reiniciá QZ Tray (ícono en la bandeja del sistema → Exit, y abrilo de nuevo).</p>
          <p>4. Recargá esta página y conectá — no debería volver a aparecer el aviso.</p>
        </div>
      </div>
    </div>
  );
}

function DriverCard({
  name,
  logo,
  description,
  url,
  compat,
  popular,
}: {
  name: string;
  logo: string;
  description: string;
  url: string;
  compat: string[];
  popular: boolean;
}) {
  return (
    <div className="glass-card rounded-xl p-4 space-y-3 relative">
      {popular && (
        <Badge className="absolute right-3 top-3 text-[10px]">Popular</Badge>
      )}
      <div className="text-2xl">{logo}</div>
      <div>
        <p className="text-sm font-semibold text-foreground">{name}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <div className="flex flex-wrap gap-1">
        {compat.map((c) => (
          <Badge key={c} variant="outline" className="text-[10px]">
            {c}
          </Badge>
        ))}
      </div>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
      >
        <Download className="h-3 w-3" />
        Descargar driver
      </a>
    </div>
  );
}

function PrinterRoleCard({
  role,
  cfg,
  foundPrinters,
  testPrinting,
  isReady,
  onUpdate,
  onRemove,
  onTest,
}: {
  role: PrinterRole;
  cfg?: QzPrinterConfig;
  foundPrinters: string[];
  testPrinting: boolean;
  isReady: boolean;
  onUpdate: (field: keyof QzPrinterConfig, value: unknown) => void;
  onRemove: () => void;
  onTest: () => void;
}) {
  const [expanded, setExpanded] = useState(!!cfg?.printerName);

  const isConfigured = !!cfg?.printerName;

  // Only show discovered printers in the dropdown — NOT the user's typed name
  // (adding typed name caused the select to appear after 1 char, breaking input)
  const scannedPrinters = foundPrinters;

  return (
    <div
      className={cn(
        "glass-card rounded-xl overflow-hidden border",
        isConfigured ? "border-primary/20" : "border-border/40",
      )}
    >
      {/* Header */}
      <button
        type="button"
        className="w-full flex items-center gap-3 px-5 py-4 text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        <Printer
          className={cn(
            "h-4 w-4 shrink-0",
            isConfigured ? "text-primary" : "text-muted-foreground",
          )}
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">
            {PRINTER_ROLE_LABELS[role]}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {isConfigured ? cfg!.printerName : PRINTER_ROLE_DESCRIPTIONS[role]}
          </p>
        </div>
        {isConfigured ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
        ) : (
          <XCircle className="h-4 w-4 text-muted-foreground/40 shrink-0" />
        )}
      </button>

      {/* Config panel */}
      {expanded && (
        <div className="border-t border-border/30 px-5 py-4 space-y-4">

          {/* Printer selector */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">
              Nombre de impresora
            </Label>
            {/* Text input always visible — datalist for autocomplete if scan found printers */}
            <div className="flex gap-2">
              <input
                type="text"
                list={scannedPrinters.length > 0 ? `printers-${role}` : undefined}
                placeholder="Ej: 3nstar RPT008"
                value={cfg?.printerName ?? ""}
                onChange={(e) => onUpdate("printerName", e.target.value)}
                className="flex-1 h-9 rounded-md border border-input bg-background px-3 py-1 text-sm font-mono placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring"
              />
              {scannedPrinters.length > 0 && (
                <datalist id={`printers-${role}`}>
                  {scannedPrinters.map((p) => (
                    <option key={p} value={p} />
                  ))}
                </datalist>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {scannedPrinters.length > 0
                ? "Seleccioná de las detectadas o escribí el nombre exacto."
                : "Escribí el nombre exactamente como aparece en Sistema → Impresoras y Escáneres."}
            </p>
          </div>

          {/* Paper width */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Ancho de papel</Label>
            <div className="flex gap-2">
              {(["58mm", "80mm"] as const).map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => onUpdate("paperWidth", w)}
                  className={cn(
                    "flex-1 rounded-lg border py-2 text-sm font-medium transition-colors",
                    (cfg?.paperWidth ?? "80mm") === w
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border/50 text-muted-foreground hover:border-primary/40",
                  )}
                >
                  {w}
                </button>
              ))}
            </div>
          </div>

          {/* Copies + Auto-cut */}
          <div className="flex items-center gap-6">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Copias</Label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="h-7 w-7 rounded border border-border/50 text-muted-foreground hover:text-foreground"
                  onClick={() => onUpdate("copies", Math.max(1, (cfg?.copies ?? 1) - 1))}
                >
                  −
                </button>
                <span className="w-6 text-center text-sm font-medium">
                  {cfg?.copies ?? 1}
                </span>
                <button
                  type="button"
                  className="h-7 w-7 rounded border border-border/50 text-muted-foreground hover:text-foreground"
                  onClick={() => onUpdate("copies", Math.min(5, (cfg?.copies ?? 1) + 1))}
                >
                  +
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-1">
              <Switch
                id={`autocut-${role}`}
                checked={cfg?.autoCut ?? true}
                onCheckedChange={(v) => onUpdate("autoCut", v)}
              />
              <Label htmlFor={`autocut-${role}`} className="text-xs text-muted-foreground cursor-pointer">
                Corte automático
              </Label>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id={`enabled-${role}`}
                checked={cfg?.enabled ?? true}
                onCheckedChange={(v) => onUpdate("enabled", v)}
              />
              <Label htmlFor={`enabled-${role}`} className="text-xs text-muted-foreground cursor-pointer">
                Activa
              </Label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1" data-tour="printer-test">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-2"
              disabled={testPrinting || !isConfigured || !isReady}
              onClick={onTest}
            >
              {testPrinting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <PlayCircle className="h-3.5 w-3.5" />
              )}
              {testPrinting ? "Imprimiendo…" : "Imprimir prueba"}
            </Button>

            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="ml-auto gap-1.5 text-muted-foreground hover:text-destructive"
              onClick={onRemove}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Quitar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
